export const LAYER_ROLES = {
  text: [
    { value: 'text', label: 'Texto' },
    { value: 'title', label: 'Título' },
    { value: 'field', label: 'Campo' },
    { value: 'header', label: 'Cabeçalho' },
    { value: 'footer', label: 'Rodapé' },
  ],
  image: [
    { value: 'image', label: 'Imagem' },
    { value: 'background', label: 'Imagem de fundo' },
    { value: 'page', label: 'Página original' },
  ],
  line: [{ value: 'line', label: 'Linha divisória' }],
}

function explicitRole(element) {
  return LAYER_ROLES[element?.type]?.some(
    ({ value }) => value === element.layerRole,
  )
    ? element.layerRole
    : ''
}

function cleanName(value) {
  return typeof value === 'string'
    ? value
        .replace(/[<>]/g, '')
        .replace(/[\s\p{Cc}]+/gu, ' ')
        .trim()
        .slice(0, 100)
    : ''
}

export function normalizeLayerMetadata(type, input = {}) {
  return {
    // An empty role remains eligible for classification once page context is
    // available. The default text type is not an explicit semantic decision.
    layerRole: explicitRole({ type, layerRole: input.layerRole }),
    layerName: cleanName(input.layerName),
  }
}

export function getLayerRole(element) {
  return explicitRole(element) || LAYER_ROLES[element?.type]?.[0].value || ''
}

export function getLayerLabel(element) {
  return (
    LAYER_ROLES[element?.type]?.find(
      ({ value }) => value === getLayerRole(element),
    )?.label || 'Elemento'
  )
}

export function getLayerName(element) {
  return cleanName(element?.layerName) || getLayerLabel(element)
}

function textOf(element) {
  return typeof element.text === 'string' ? element.text.trim() : ''
}

function isField(text) {
  return /^(?:{{\s*[\w.:-]+\s*}}\s*)+$/.test(text)
}

function bodyFontSize(elements) {
  const candidates = elements.filter((element) => !isField(textOf(element)))
  // Character weighting keeps many short headings/page numbers from outweighing
  // the body. Cap each run so a manually created large paragraph cannot dominate.
  const sizes = candidates
    .map((element) => ({
      size: element.fontSize,
      weight: Math.min(textOf(element).length, 400),
    }))
    .filter(({ size, weight }) => Number.isFinite(size) && size > 0 && weight > 0)
    .sort((left, right) => left.size - right.size)
  let remaining = sizes.reduce((sum, { weight }) => sum + weight, 0) / 2
  for (const { size, weight } of sizes) {
    remaining -= weight
    if (remaining <= 0) return size
  }
  return 12
}

function isTitle(element, texts, bodySize) {
  const text = textOf(element)
  if (
    !text ||
    text.length > 120 ||
    text.split(/\s+/).length > 16 ||
    text.split(/\r?\n/).filter(Boolean).length > 2 ||
    /[.!?;,]$/.test(text) ||
    !/\p{L}/u.test(text)
  )
    return false

  const others = texts.filter((other) => other !== element)
  // PDF extraction can split one sentence into several styles. A bold fragment
  // sharing its line with another run is not independently a heading.
  const sharesLine = others.some((other) => {
    const overlap =
      Math.min(element.y + element.height, other.y + other.height) -
      Math.max(element.y, other.y)
    return overlap > Math.min(element.height, other.height) * 0.5
  })
  if (sharesLine) return false

  if (element.fontSize >= bodySize + 2 && element.fontSize >= bodySize * 1.15)
    return true
  if (!element.bold || element.fontSize < bodySize * 0.95) return false
  if (text === text.toLocaleUpperCase('pt-BR')) return true

  const isolated = others.every((other) => {
    const gap = Math.max(
      other.y - (element.y + element.height),
      element.y - (other.y + other.height),
    )
    return gap >= 2
  })
  return isolated && text.length <= 70 && text.split(/\s+/).length <= 10
}

export function classifyPageLayers(elements) {
  if (!Array.isArray(elements)) return []
  const texts = elements.filter(
    (element) => element?.type === 'text' && textOf(element),
  )
  const bodySize = bodyFontSize(texts)
  return elements.map((element) => {
    const metadata = normalizeLayerMetadata(element.type, element)
    if (metadata.layerRole) return { ...element, ...metadata }
    let role = getLayerRole(element)
    if (element.type === 'text') {
      const text = textOf(element)
      if (isField(text)) role = 'field'
      else if (
        text &&
        text.length <= 180 &&
        element.fontSize <= bodySize * 1.05 &&
        element.y >= 277
      )
        role = 'footer'
      else if (
        text &&
        text.length <= 120 &&
        element.fontSize <= bodySize * 1.05 &&
        element.y <= 15 &&
        element.y + element.height <= 22
      )
        role = 'header'
      else if (isTitle(element, texts, bodySize)) role = 'title'
    }
    // Locked images may be logos, photos or scanned pages. Only the importer or
    // an explicit user choice can identify their background/original-page role.
    return { ...element, ...metadata, layerRole: role }
  })
}
